import React, { useEffect, useRef } from 'react';
import { FaBold, FaItalic, FaListOl, FaListUl } from 'react-icons/fa';
import './MarkdownEditor.scss';

type MarkdownEditorProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  isHeader: boolean;
};

const MarkdownEditor: React.FC<MarkdownEditorProps> = ({
  value,
  onChange,
  placeholder,
  isHeader
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const wrapSelection = (left: string, right: string = left) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const { selectionStart, selectionEnd } = textarea;
    const selected = value.slice(selectionStart, selectionEnd);
    const before = value.slice(0, selectionStart);
    const after = value.slice(selectionEnd);

    const newValue = `${before}${left}${selected}${right}${after}`;
    onChange(newValue);

    // Move cursor after the inserted markup
    requestAnimationFrame(() => {
      const pos = selectionStart + left.length + selected.length + right.length;
      textarea.setSelectionRange(pos, pos);
      textarea.focus();
    });
  };

  const prependLine = (prefix: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const { selectionStart, selectionEnd, value } = textarea;

    const lines = value
      .substring(selectionStart, selectionEnd || value.length)
      .split('\n')
      .map((line) => prefix + line);

    const newText = lines.join('\n');

    const before = value.substring(0, selectionStart);
    const after = value.substring(selectionEnd);

    const updated = before + newText + after;
    onChange(updated);

    // Restore selection
    const cursor = selectionStart + prefix.length;
    requestAnimationFrame(() => {
      textarea.selectionStart = cursor;
      textarea.selectionEnd = cursor;
      textarea.focus();
    });
  };

  // Keyboard shortcuts
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        if (e.key === 'b' || e.key === 'B') {
          e.preventDefault();
          wrapSelection('**');
        }
        if (e.key === 'i' || e.key === 'I') {
          e.preventDefault();
          wrapSelection('*');
        }
      }
    };

    textarea.addEventListener('keydown', handleKeyDown);
    return () => {
      textarea.removeEventListener('keydown', handleKeyDown);
    };
  }, [value]);

  return (
    <div className="markdown-editor">
      {!isHeader && (
        <div className="markdown-editor__toolbar">
          <button
            type="button"
            onClick={() => wrapSelection('**')}
            title="Bold (Ctrl+B)"
          >
            <FaBold />
          </button>
          <button
            type="button"
            onClick={() => wrapSelection('*')}
            title="Italic (Ctrl+I)"
          >
            <FaItalic />
          </button>
          <button
            type="button"
            onClick={() => prependLine('- ')}
            title="Bullet List (Ctrl+L)"
          >
            <FaListUl />
          </button>
          <button
            type="button"
            onClick={() => prependLine('1. ')}
            title="Numbered List"
          >
            <FaListOl />
          </button>
        </div>
      )}
      <textarea
        ref={textareaRef}
        className="markdown-editor__textarea"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || 'Write in Markdown...'}
      />
    </div>
  );
};

export default MarkdownEditor;
